const SUFFIXES = ['', 'K', 'M', 'B', 'T', 'Qa', 'Qi', 'Sx', 'Sp', 'Oc', 'No', 'Dc'];

/** A tiny base-10 magnitude type that stays useful beyond Number's display range. */
export class Magnitude {
  readonly mantissa: number;
  readonly exponent: number;

  private constructor(mantissa: number, exponent: number) {
    if (!Number.isFinite(mantissa) || mantissa <= 0) {
      this.mantissa = 0;
      this.exponent = 0;
      return;
    }
    const shift = Math.floor(Math.log10(Math.abs(mantissa)));
    this.mantissa = mantissa / 10 ** shift;
    this.exponent = exponent + shift;
  }

  static zero(): Magnitude {
    return new Magnitude(0, 0);
  }

  static from(value: number | string | Magnitude): Magnitude {
    if (value instanceof Magnitude) return value;
    if (typeof value === 'number') {
      if (value <= 0 || Number.isNaN(value)) return Magnitude.zero();
      const exponent = Math.floor(Math.log10(value));
      return new Magnitude(value / 10 ** exponent, exponent);
    }
    const match = value.trim().match(/^([+]?[\d.]+)(?:e([+-]?\d+))?$/i);
    if (!match) return Magnitude.zero();
    return new Magnitude(Number(match[1]), Number(match[2] ?? 0));
  }

  add(otherValue: number | string | Magnitude): Magnitude {
    const other = Magnitude.from(otherValue);
    if (this.mantissa === 0) return other;
    if (other.mantissa === 0) return this;
    const larger = this.exponent >= other.exponent ? this : other;
    const smaller = larger === this ? other : this;
    const delta = larger.exponent - smaller.exponent;
    if (delta > 16) return larger;
    return new Magnitude(larger.mantissa + smaller.mantissa * 10 ** -delta, larger.exponent);
  }

  subtract(otherValue: number | string | Magnitude): Magnitude {
    const other = Magnitude.from(otherValue);
    if (other.mantissa === 0) return this;
    if (this.compare(other) <= 0) return Magnitude.zero();
    const delta = this.exponent - other.exponent;
    if (delta > 16) return this;
    return new Magnitude(this.mantissa - other.mantissa * 10 ** -delta, this.exponent);
  }

  multiply(factor: number): Magnitude {
    return factor <= 0 ? Magnitude.zero() : new Magnitude(this.mantissa * factor, this.exponent);
  }

  divide(divisor: number): Magnitude {
    return divisor <= 0 ? this : new Magnitude(this.mantissa / divisor, this.exponent);
  }

  compare(otherValue: number | string | Magnitude): number {
    const other = Magnitude.from(otherValue);
    if (this.exponent !== other.exponent) return this.exponent > other.exponent ? 1 : -1;
    if (this.mantissa === other.mantissa) return 0;
    return this.mantissa > other.mantissa ? 1 : -1;
  }

  gte(other: number | string | Magnitude): boolean {
    return this.compare(other) >= 0;
  }

  toNumber(): number {
    if (this.mantissa === 0) return 0;
    if (this.exponent > 308) return Number.MAX_VALUE;
    return this.mantissa * 10 ** this.exponent;
  }

  log10(): number {
    return this.mantissa === 0 ? 0 : Math.log10(this.mantissa) + this.exponent;
  }

  toString(): string {
    if (this.mantissa === 0) return '0';
    return `${Number(this.mantissa.toPrecision(15))}e${this.exponent}`;
  }

  toJSON(): string {
    return this.toString();
  }

  format(mode: 'suffix' | 'scientific' | 'full' = 'suffix', decimals = 1): string {
    if (this.mantissa === 0) return '0';
    if (mode === 'scientific' || this.exponent >= SUFFIXES.length * 3) {
      return `${this.mantissa.toFixed(decimals)}e${this.exponent}`;
    }
    if (mode === 'full' && this.exponent <= 15) {
      return Math.floor(this.toNumber()).toLocaleString('en-US');
    }
    const tier = Math.floor(this.exponent / 3);
    if (tier === 0) return Math.floor(this.toNumber()).toLocaleString('en-US');
    const scaled = this.mantissa * 10 ** (this.exponent - tier * 3);
    return `${scaled.toFixed(scaled >= 100 ? 0 : decimals)}${SUFFIXES[tier]}`;
  }
}
